import { Injectable, inject, signal, computed } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Observable, filter, map, fromEvent, EMPTY } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Serviço PWA para gerenciar funcionalidades básicas
 * Seguindo as regras do projeto: métodos em português, signals do Angular
 */
@Injectable({
  providedIn: 'root'
})
export class PWAService {
  private readonly swUpdate = inject(SwUpdate, { optional: true });

  // Estado reativo com signals
  private readonly _isUpdateAvailable = signal<boolean>(false);
  private readonly _isOnline = signal<boolean>(navigator.onLine);
  private readonly _isInstallable = signal<boolean>(false);
  private readonly _isInstalled = signal<boolean>(false);

  // Computed signals públicos
  readonly isUpdateAvailable = computed(() => this._isUpdateAvailable());
  readonly isOnline = computed(() => this._isOnline());
  readonly isInstallable = computed(() => this._isInstallable());
  readonly isInstalled = computed(() => this._isInstalled());

  // Prompt de instalação
  private deferredPrompt: any = null;

  constructor() {
    this.initializeService();
  }

  /**
   * Inicializa o serviço PWA
   */
  private initializeService(): void {
    // Verificar se PWA está habilitado no environment
    if (!environment.pwa.enabled) {
      return;
    }

    // Verificar se service worker está disponível
    if (!this.swUpdate?.isEnabled) {
      return;
    }

    // Logs para desenvolvimento
    if (environment.pwa.devMode) {
      console.log('🚀 PWA Service iniciado em modo desenvolvimento');
      console.log('📱 Configurações:', {
        cacheApi: environment.pwa.cacheApi,
        showInstallPrompt: environment.pwa.showInstallPrompt,
        cacheStrategy: environment.pwa.cacheStrategy
      });
    }

    // Escutar mudanças de conexão
    this.listenToNetworkStatus();

    // Verificar atualizações (com debounce em desenvolvimento)
    this.checkForUpdates();

    // Escutar evento de instalação (sempre disponível)
    if (environment.pwa.showInstallPrompt) {
      this.listenToInstallPrompt();
    }

    // Verificar se já está instalado
    this.checkIfInstalled();
  }

  /**
   * Escuta mudanças no status da rede
   */
  private listenToNetworkStatus(): void {
    fromEvent(window, 'online').subscribe(() => {
      this._isOnline.set(true);
    });

    fromEvent(window, 'offline').subscribe(() => {
      this._isOnline.set(false);
    });
  }

  /**
   * Verifica se há atualizações disponíveis
   */
  private checkForUpdates(): void {
    if (!this.swUpdate) return;

    this.swUpdate.versionUpdates
      .pipe(
        filter((evt: any): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      )
      .subscribe(() => {
        this._isUpdateAvailable.set(true);
      });
  }

  /**
   * Escuta o prompt de instalação
   */
  private listenToInstallPrompt(): void {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this._isInstallable.set(true);
    });
  }

  /**
   * Verifica se a PWA já está instalada
   */
  private checkIfInstalled(): void {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      this._isInstalled.set(true);
    }
  }

  /**
   * Aplica atualização disponível
   * @returns Promise<boolean> - Sucesso da atualização
   */
  async aplicarAtualizacao(): Promise<boolean> {
    if (!this.swUpdate || !this.isUpdateAvailable()) {
      return false;
    }

    try {
      await this.swUpdate.activateUpdate();
      this._isUpdateAvailable.set(false);
      // Recarregar a página após aplicar a atualização
      document.location.reload();
      return true;
    } catch (error) {
      console.error('Erro ao aplicar atualização:', error);
      return false;
    }
  }

  /**
   * Força verificação de atualizações
   * @returns Promise<boolean> - Se há atualização disponível
   */
  async verificarAtualizacoes(): Promise<boolean> {
    if (!this.swUpdate) {
      return false;
    }

    try {
      const hasUpdate = await this.swUpdate.checkForUpdate();
      if (hasUpdate) {
        this._isUpdateAvailable.set(true);
      }
      return hasUpdate;
    } catch (error) {
      console.error('Erro ao verificar atualizações:', error);
      return false;
    }
  }

  /**
   * Mostra prompt de instalação
   * @returns Promise<boolean> - Se o usuário aceitou instalar
   */
  async mostrarPromptInstalacao(): Promise<boolean> {
    if (!this.deferredPrompt || !this.isInstallable()) {
      return false;
    }

    const result = await this.deferredPrompt.prompt();
    const userChoice = await result.userChoice;

    if (userChoice === 'accepted') {
      this._isInstallable.set(false);
      this._isInstalled.set(true);
      this.deferredPrompt = null;
      return true;
    }

    return false;
  }

  /**
   * Verifica se PWA está rodando no modo standalone
   * @returns boolean
   */
  isStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches;
  }

  /**
   * Obtém informações sobre a conexão
   * @returns Objeto com informações da conexão
   */
  obterInfoConexao(): { 
    online: boolean; 
    type?: string; 
    effectiveType?: string; 
    downlink?: number; 
  } {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    return {
      online: this.isOnline(),
      type: connection?.type,
      effectiveType: connection?.effectiveType,
      downlink: connection?.downlink
    };
  }

  /**
   * Limpa cache do service worker
   * @returns Promise<void>
   */
  async limparCache(): Promise<void> {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.unregister();
        window.location.reload();
      }
    }
  }

  /**
   * Métodos para desenvolvimento
   */
  
  /**
   * Limpa todo o cache do Service Worker (útil para desenvolvimento)
   */
  async limparCacheDesenvolvimento(): Promise<void> {
    if (!environment.pwa.devMode) {
      console.warn('Método disponível apenas em modo desenvolvimento');
      return;
    }

    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('🧹 Cache limpo com sucesso');
      
      // Recarregar página para aplicar mudanças
      window.location.reload();
    } catch (error) {
      console.error('Erro ao limpar cache:', error);
    }
  }

  /**
   * Obtém informações detalhadas sobre o cache (debugging)
   */
  async obterInfoCache(): Promise<any> {
    if (!environment.pwa.devMode) {
      console.warn('Método disponível apenas em modo desenvolvimento');
      return null;
    }

    try {
      const cacheNames = await caches.keys();
      const cacheInfo = await Promise.all(
        cacheNames.map(async (name) => {
          const cache = await caches.open(name);
          const keys = await cache.keys();
          return {
            name,
            size: keys.length,
            urls: keys.map(req => req.url)
          };
        })
      );
      
      console.log('📊 Informações do cache:', cacheInfo);
      return cacheInfo;
    } catch (error) {
      console.error('Erro ao obter informações do cache:', error);
      return null;
    }
  }

  /**
   * Força atualização do Service Worker
   */
  async forcarAtualizacao(): Promise<void> {
    if (!this.swUpdate?.isEnabled) return;

    try {
      await this.swUpdate.checkForUpdate();
      console.log('🔄 Verificação de atualização forçada');
    } catch (error) {
      console.error('Erro ao forçar atualização:', error);
    }
  }
} 